/**
 * Optional TMOS hierarchy scope for deployed agent instances.
 * Uses scoped hierarchy lists — server validates on create/update.
 */

import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { hierarchyService, type HierarchyOption } from '@/services/hierarchyService';

export interface InstanceHierarchyScope {
  businessHierarchyId: string;
  functionalHierarchyId: string;
  geographicHierarchyId: string;
}

interface InstanceHierarchyScopeFieldsProps {
  value: InstanceHierarchyScope;
  disabled?: boolean;
  onChange: (next: InstanceHierarchyScope) => void;
}

const FIELD_CLASS =
  'w-full rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-3 py-2 text-base text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]';
const LABEL_CLASS = 'mb-1 block text-base font-medium text-[var(--color-surface-text)]';

export const InstanceHierarchyScopeFields: React.FC<InstanceHierarchyScopeFieldsProps> = ({
  value,
  disabled = false,
  onChange,
}) => {
  const { t } = useLanguage();
  const [business, setBusiness] = useState<HierarchyOption[]>([]);
  const [functional, setFunctional] = useState<HierarchyOption[]>([]);
  const [geographic, setGeographic] = useState<HierarchyOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      hierarchyService.getBusinessHierarchyScoped(),
      hierarchyService.getFunctionalHierarchyScoped(),
      hierarchyService.getGeographicHierarchyScoped(),
    ])
      .then(([b, f, g]) => {
        if (cancelled) return;
        setBusiness(b);
        setFunctional(f);
        setGeographic(g);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <fieldset className="rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] p-3">
      <legend className={`${LABEL_CLASS} px-1`}>{t('aiStudio.instances.form.hierarchyScope')}</legend>
      <p className="mb-3 text-sm text-[var(--color-surface-text-secondary)]">
        {t('aiStudio.instances.form.hierarchyScopeHint')}
      </p>
      <div className="grid gap-3 md:grid-cols-3">
        <div>
          <label className={LABEL_CLASS} htmlFor="instance-business-hierarchy">
            {t('aiStudio.instances.form.businessHierarchy')}
          </label>
          <select
            id="instance-business-hierarchy"
            className={FIELD_CLASS}
            value={value.businessHierarchyId}
            disabled={disabled || loading}
            onChange={(e) =>
              onChange({ ...value, businessHierarchyId: e.target.value })
            }
          >
            <option value="">{t('aiStudio.common.none')}</option>
            {business.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={LABEL_CLASS} htmlFor="instance-functional-hierarchy">
            {t('aiStudio.instances.form.functionalHierarchy')}
          </label>
          <select
            id="instance-functional-hierarchy"
            className={FIELD_CLASS}
            value={value.functionalHierarchyId}
            disabled={disabled || loading}
            onChange={(e) =>
              onChange({ ...value, functionalHierarchyId: e.target.value })
            }
          >
            <option value="">{t('aiStudio.common.none')}</option>
            {functional.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={LABEL_CLASS} htmlFor="instance-geographic-hierarchy">
            {t('aiStudio.instances.form.geographicHierarchy')}
          </label>
          <select
            id="instance-geographic-hierarchy"
            className={FIELD_CLASS}
            value={value.geographicHierarchyId}
            disabled={disabled || loading}
            onChange={(e) =>
              onChange({ ...value, geographicHierarchyId: e.target.value })
            }
          >
            <option value="">{t('aiStudio.common.none')}</option>
            {geographic.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </fieldset>
  );
};
